import React, { useEffect, useState } from 'react';
import { User } from '../types';
import * as db from '../services/db';
import { usePos } from '../context/PosContext';
import '../pages/QrLogin.css';
import './LoginScreen.css';

// Full-screen operator login shown by the LoginGate (App.tsx) whenever active
// users exist but nobody is logged in. Same Quick-PIN pad as the QR Login
// page; on success db.setCurrentUserId fires 'sconnect:auth' and the gate
// unlocks the app shell.
const LoginScreen: React.FC = () => {
  const { config } = usePos();
  const [now, setNow] = useState(new Date());
  const [users, setUsers] = useState<User[]>([]);
  const [selUserId, setSelUserId] = useState<string>('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);

  // Load active operators once; default the selection to the first.
  useEffect(() => {
    let alive = true;
    db.getUsers().then((all) => {
      if (!alive) return;
      const active = all.filter((u) => u.active);
      setUsers(active);
      setSelUserId((cur) => cur || active[0]?.id || '');
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const pressKey = (d: string) => {
    setPinError(false);
    setPin((p) => (p.length >= 6 ? p : p + d));
  };
  const backspace = () => {
    setPinError(false);
    setPin((p) => p.slice(0, -1));
  };

  const doLogin = () => {
    const user = users.find((u) => u.id === selUserId);
    if (!user) return;
    if (pin.length < 4) {
      setPinError(true);
      return;
    }
    if (pin !== user.pin) {
      setPinError(true);
      setPin('');
      return;
    }
    db.logEvent({ type: 'login', detail: `Logged in as ${user.name} (Quick PIN)`, userName: user.name });
    db.setCurrentUserId(user.id);
  };

  // Physical keyboard: digits, Backspace, Enter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) pressKey(e.key);
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Enter') doLogin();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pin, selUserId, users]);

  return (
    <div className="ql-page ls-page">
      <div className="ql-clock">{now.toLocaleString(undefined, {
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })}</div>

      <div className="ql-card ls-card">
        <div className="ql-right">
          <div className="ql-pin">
            <div className="ls-logo">EP</div>
            <div className="ls-outlet">{config.storeName || 'EPOS'}</div>
            <div className="ql-pin-title">Operator login required</div>

            <select
              className="ql-user-select"
              value={selUserId}
              onChange={(e) => { setSelUserId(e.target.value); setPin(''); setPinError(false); }}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>

            <div className={`ql-pin-display ${pinError ? 'err' : ''}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className={`ql-pin-dot ${i < pin.length ? 'on' : ''}`} />
              ))}
            </div>
            {pinError && <div className="ql-pin-err">Wrong PIN — try again</div>}

            <div className="ql-keypad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => (
                <button key={k} className="ql-key" onClick={() => pressKey(k)}>{k}</button>
              ))}
              <button className="ql-key ql-key-fn" onClick={backspace}>⌫</button>
              <button className="ql-key" onClick={() => pressKey('0')}>0</button>
              <button className="ql-key ql-key-enter" onClick={doLogin}>↵</button>
            </div>

            <p className="ql-pin-foot muted">Enter your Quick PIN to open the POS.</p>
            {users.find((u) => u.id === selUserId && u.id === 'admin' && u.pin === db.DEFAULT_ADMIN_PIN) && (
              <p className="ql-pin-foot muted ls-default-hint">
                First run? Default PIN is {db.DEFAULT_ADMIN_PIN} — change it under Users.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="ql-footer ls-footer">
        <span>{config.storeName}{config.address ? ` · ${config.address}` : ''}</span>
      </div>
    </div>
  );
};

export default LoginScreen;
